import { useEffect, useState } from 'react';
import {
  api, type ComedyAnalytics, type ComedyChannel, type ComedyPlatform, type ComedyProduction, type ComedyProductionDetail,
  type ComedyPublication,
} from '../../lib/api';
import { Button, ErrorAlert, Spinner } from '../../components/ui';
import {
  COMEDY_PLATFORM_LABEL, ComedyEmpty, ComedySection, ComedyStat, ComedyStatus, Field, formatDate, formatDuration, formatUsd,
} from './ComedyShared';

const ALL_PLATFORMS: ComedyPlatform[] = ['YOUTUBE', 'FACEBOOK', 'INSTAGRAM', 'TIKTOK'];

function PlatformPicker({ value, onChange }: { value: ComedyPlatform[]; onChange: (next: ComedyPlatform[]) => void }) {
  return (
    <div className="inline" style={{ flexWrap: 'wrap', gap: 12 }}>
      {ALL_PLATFORMS.map((platform) => (
        <label key={platform} className="inline">
          <input type="checkbox" checked={value.includes(platform)} onChange={(event) => onChange(event.target.checked ? [...value, platform] : value.filter((item) => item !== platform))} />
          {COMEDY_PLATFORM_LABEL[platform]}
        </label>
      ))}
    </div>
  );
}

export function ComedyVideoStudioPanel({ channel }: { channel: ComedyChannel }) {
  const [productions, setProductions] = useState<ComedyProduction[]>([]);
  const [detail, setDetail] = useState<ComedyProductionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [platforms, setPlatforms] = useState<ComedyPlatform[]>(['YOUTUBE', 'FACEBOOK', 'INSTAGRAM', 'TIKTOK']);

  const load = async () => {
    setLoading(true); setError(null);
    try { setProductions((await api.comedy.productions(channel.id)).productions); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id]);

  const open = async (production: ComedyProduction) => {
    setError(null);
    try { setDetail(await api.comedy.production(channel.id, production.id)); } catch (err) { setError(err); }
  };

  const approve = async () => {
    if (!detail) return;
    setBusy(true); setError(null);
    try { await api.comedy.approveProduction(channel.id, detail.production.id); setMessage('Film zatwierdzony. Możesz przygotować publikacje.'); setDetail(await api.comedy.production(channel.id, detail.production.id)); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!detail) return;
    const reason = window.prompt('Powód odrzucenia filmu:', 'Wymaga poprawy montażu');
    if (!reason) return;
    setBusy(true);
    try { await api.comedy.rejectProduction(channel.id, detail.production.id, reason); setDetail(null); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const prepare = async () => {
    if (!detail) return;
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.preparePublications(channel.id, { productionId: detail.production.id, platforms: platforms.length ? platforms : undefined });
      setMessage(`Przygotowano ${result.publications.length} publikacji (osobne opisy dla każdej platformy). Przejdź do Harmonogramu, aby je zaplanować.`);
      setDetail(await api.comedy.production(channel.id, detail.production.id));
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection title="Video Studio" description="Gotowe rendery z QA, podglądem i zatwierdzaniem. Publikacja nie uruchamia się automatycznie w trybie SEMI-AUTO." actions={<Button size="sm" onClick={() => void load()}>Odśwież</Button>}>
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        {loading ? <Spinner label="Ładowanie produkcji…" /> : productions.length === 0 ? (
          <ComedyEmpty title="Brak produkcji" hint="Zatwierdź scenariusz i uruchom render w Comedy Directorze." />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Scenariusz</th><th>Status</th><th>Render</th><th>QA</th><th>Długość</th><th>Koszt AI</th><th /></tr></thead>
              <tbody>
                {productions.map((production) => (
                  <tr key={production.id}>
                    <td><strong>{production.scenarioTitle ?? production.scenarioId}</strong><div className="muted" style={{ fontSize: 11 }}>{production.scenarioFormat ?? ''} · {formatDate(production.createdAt)}</div></td>
                    <td><ComedyStatus value={production.status} />{production.lastError && <div className="muted" style={{ fontSize: 11, color: 'var(--danger)' }}>{production.lastError.slice(0, 120)}</div>}</td>
                    <td>{production.videoJobStatus ?? '—'}{production.videoJobProgress != null ? ` · ${production.videoJobProgress}%` : ''}</td>
                    <td>{production.qaVerdict ? <><ComedyStatus value={production.qaVerdict} /><div className="muted" style={{ fontSize: 11 }}>{production.qaScore != null ? Math.round(production.qaScore) : '—'}/100</div></> : '—'}</td>
                    <td>{formatDuration(production.durationSeconds)}</td>
                    <td>{formatUsd(production.costUsd)}</td>
                    <td><Button size="sm" onClick={() => void open(production)}>Podgląd</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>

      {detail && (
        <ComedySection
          title={<span>Produkcja <ComedyStatus value={detail.production.status} /></span>}
          description={detail.scenario ? `${detail.scenario.title} · ${detail.scenario.format}` : detail.production.id}
          actions={<div className="btn-row"><Button size="sm" onClick={() => setDetail(null)}>Zamknij</Button>{detail.production.status === 'READY' && <><Button size="sm" variant="success" onClick={() => void approve()} disabled={busy}>Zatwierdź film</Button><Button size="sm" variant="danger" onClick={() => void reject()} disabled={busy}>Odrzuć</Button></>}{detail.production.status === 'APPROVED' && <Button size="sm" variant="primary" onClick={() => void prepare()} disabled={busy}>Przygotuj publikacje</Button>}{detail.production.status === 'FAILED' && <Button size="sm" onClick={() => void load()}>Odśwież</Button>}</div>}
        >
          <div className="grid grid--2">
            <div>
              {detail.production.videoUrl ? (
                <video controls playsInline style={{ width: '100%', maxHeight: 560, background: '#000', borderRadius: 10 }} src={detail.production.videoUrl} />
              ) : <div className="empty"><div className="empty__icon">▶</div><div>Render nie jest jeszcze gotowy ({detail.production.status}).</div></div>}
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Długość: {formatDuration(detail.production.durationSeconds)} · styl napisów: {detail.production.captionStyle} · hash: {detail.production.renderHash.slice(0, 12)}…
              </div>
            </div>
            <div>
              <div className="grid grid--2">
                <ComedyStat label="QA score" value={detail.qa ? Math.round(detail.qa.score) : '—'} hint={detail.qa?.verdict ?? 'brak QA'} tone={detail.qa?.verdict === 'FAIL' ? 'danger' : detail.qa?.verdict === 'WARN' ? 'warning' : 'success'} />
                <ComedyStat label="Koszt AI" value={formatUsd(detail.production.costUsd)} />
              </div>
              {detail.qa ? (
                <div className="table-wrap" style={{ marginTop: 10, overflowX: 'auto' }}>
                  <table className="table">
                    <thead><tr><th>Kryterium</th><th>Wynik</th><th>Status</th><th>Szczegóły</th></tr></thead>
                    <tbody>
                      {detail.qa.criteria.map((criterion) => (
                        <tr key={criterion.key}>
                          <td>{criterion.label}</td><td>{criterion.points}/{criterion.weight}</td>
                          <td><ComedyStatus value={criterion.status} /></td><td className="muted" style={{ fontSize: 11 }}>{criterion.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <div className="muted" style={{ marginTop: 10 }}>QA uruchomi się automatycznie po zakończeniu renderu.</div>}
              {detail.qa?.notes && <div className="alert alert--error" style={{ marginTop: 10 }}>{detail.qa.notes}</div>}
              {detail.production.status === 'APPROVED' && (
                <div style={{ marginTop: 12 }}>
                  <Field label="Platformy do publikacji"><PlatformPicker value={platforms} onChange={setPlatforms} /></Field>
                  <div className="muted" style={{ fontSize: 12 }}>Opisy, hashtagi i CTA są generowane osobno dla każdej platformy. Profil musi być przypisany do kanału.</div>
                </div>
              )}
            </div>
          </div>

          {detail.publications.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Przygotowane publikacje</div>
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead><tr><th>Platforma</th><th>Profil</th><th>Tytuł</th><th>Status</th><th>Termin</th></tr></thead>
                  <tbody>{detail.publications.map((publication) => (
                    <tr key={publication.id}>
                      <td>{COMEDY_PLATFORM_LABEL[publication.platform] ?? publication.platform}</td>
                      <td>{publication.accountName ?? publication.socialAccountId}</td>
                      <td style={{ maxWidth: 280 }}>{publication.title}</td>
                      <td><ComedyStatus value={publication.status} />{publication.error && <div className="muted" style={{ fontSize: 11, color: 'var(--danger)' }}>{publication.error.slice(0, 120)}</div>}</td>
                      <td>{formatDate(publication.scheduledAt)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
        </ComedySection>
      )}
    </div>
  );
}

export function ComedyQueuePanel({ channel }: { channel: ComedyChannel }) {
  const [productions, setProductions] = useState<ComedyProduction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [autopilot, setAutopilot] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { setProductions((await api.comedy.productions(channel.id)).productions); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id]);

  const run = async () => {
    setBusy(true); setError(null);
    try { setAutopilot(await api.comedy.runAutopilot(channel.id) as unknown as Record<string, unknown>); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const active = productions.filter((production) => ['QUEUED', 'RENDERING', 'QA_PENDING'].includes(production.status));
  const failed = productions.filter((production) => ['FAILED', 'REJECTED'].includes(production.status));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection title="Kolejka produkcji" description="Aktywne rendery, błędy i ręczne uruchomienie cyklu automatyzacji dla tego kanału." actions={<div className="btn-row"><Button size="sm" onClick={() => void load()}>Odśwież</Button><Button size="sm" variant="primary" onClick={() => void run()} disabled={busy}>{busy ? 'Uruchamianie…' : 'Uruchom cykl teraz'}</Button></div>}>
        {error ? <ErrorAlert error={error} /> : null}
        {autopilot && <pre style={{ maxHeight: 220, overflow: 'auto', background: 'var(--surface-2)', padding: 10, borderRadius: 8, fontSize: 11 }}>{JSON.stringify(autopilot, null, 2)}</pre>}
        <div className="grid grid--stats">
          <ComedyStat label="W kolejce / render" value={active.length} />
          <ComedyStat label="Gotowe do QA" value={productions.filter((production) => production.status === 'QA_PENDING').length} />
          <ComedyStat label="Błędy" value={failed.length} tone={failed.length ? 'danger' : undefined} />
        </div>
        {loading ? <Spinner label="Ładowanie kolejki…" /> : active.length === 0 ? <div className="muted">Brak aktywnych renderów. Kolejka jest pusta.</div> : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Scenariusz</th><th>Status</th><th>Postęp</th><th>Utworzono</th></tr></thead>
              <tbody>{active.map((production) => (
                <tr key={production.id}><td>{production.scenarioTitle ?? production.id}</td><td><ComedyStatus value={production.status} /></td><td>{production.videoJobProgress ?? 0}%</td><td>{formatDate(production.createdAt)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {failed.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Błędy do naprawy</div>
            {failed.map((production) => (
              <div key={production.id} className="alert alert--error">{production.scenarioTitle ?? production.id}: {production.lastError ?? 'render failed'}</div>
            ))}
          </div>
        )}
      </ComedySection>
    </div>
  );
}

export function ComedySchedulePanel({ channel }: { channel: ComedyChannel }) {
  const [productions, setProductions] = useState<ComedyProduction[]>([]);
  const [publications, setPublications] = useState<ComedyPublication[]>([]);
  const [selected, setSelected] = useState('');
  const [platforms, setPlatforms] = useState<ComedyPlatform[]>(ALL_PLATFORMS);
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [productionResponse, publicationResponse] = await Promise.all([
        api.comedy.productions(channel.id), api.comedy.publications(channel.id),
      ]);
      setProductions(productionResponse.productions.filter((production) => ['APPROVED', 'SCHEDULED', 'PUBLISHED'].includes(production.status)));
      setPublications(publicationResponse.publications);
    } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id]);

  const schedule = async () => {
    if (!selected) { setError(new Error('Wybierz zatwierdzoną produkcję.')); return; }
    setBusy(true); setError(null); setMessage('');
    try {
      await api.comedy.preparePublications(channel.id, { productionId: selected, platforms: platforms.length ? platforms : undefined });
      const result = await api.comedy.schedulePublications(channel.id, {
        productionId: selected, platforms: platforms.length ? platforms : undefined,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      const ok = result.results.filter((entry) => entry.status === 'SCHEDULED').length;
      const skipped = result.results.filter((entry) => entry.status !== 'SCHEDULED');
      setMessage(`Zaplanowano ${ok} publikacji.${skipped.length ? ` Pominięto: ${skipped.map((entry) => `${entry.platform}: ${entry.reason ?? entry.status}`).join('; ')}` : ''}`);
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const scheduled = publications.filter((publication) => ['SCHEDULED', 'APPROVED', 'DRAFT'].includes(publication.status));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection title="Harmonogram" description={`Kanał: ${channel.postsPerDay} filmów/dzień · godziny ${channel.publishTimes.join(', ')} · ${channel.timezone}. ${channel.automationMode === 'AUTOPILOT' ? 'AUTOPILOT może zaplanować zatwierdzone filmy automatycznie.' : 'W SEMI-AUTO publikacja wymaga ręcznego zatwierdzenia.'}`}>
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        <div className="form-row">
          <Field label="Zatwierdzona produkcja">
            <select value={selected} onChange={(event) => setSelected(event.target.value)}>
              <option value="">— wybierz film —</option>
              {productions.map((production) => <option key={production.id} value={production.id}>{production.scenarioTitle ?? production.id} ({production.status})</option>)}
            </select>
          </Field>
          <Field label="Konkretny termin (opcjonalnie)" hint="Puste = najbliższy wolny slot kanału/profilu."><input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} /></Field>
        </div>
        <Field label="Platformy"><PlatformPicker value={platforms} onChange={setPlatforms} /></Field>
        <div className="btn-row"><Button variant="primary" onClick={() => void schedule()} disabled={busy || !selected}>{busy ? 'Planowanie…' : 'Przygotuj i zaplanuj'}</Button><Button onClick={() => void load()}>Odśwież</Button></div>
      </ComedySection>

      <ComedySection title="Zaplanowane publikacje" description="Każdy wiersz to dokładnie jeden profil. Cron publikuje terminowe wpisy przez istniejący publisher.">
        {loading ? <Spinner label="Ładowanie harmonogramu…" /> : scheduled.length === 0 ? <div className="muted">Brak zaplanowanych publikacji.</div> : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Termin</th><th>Platforma</th><th>Profil</th><th>Tytuł</th><th>Status</th></tr></thead>
              <tbody>{scheduled.map((publication) => (
                <tr key={publication.id}>
                  <td>{formatDate(publication.scheduledAt)}</td>
                  <td>{COMEDY_PLATFORM_LABEL[publication.platform] ?? publication.platform}</td>
                  <td>{publication.accountName ?? publication.socialAccountId}</td>
                  <td style={{ maxWidth: 300 }}>{publication.title}</td>
                  <td><ComedyStatus value={publication.status} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </ComedySection>
    </div>
  );
}

export function ComedyPublicationsPanel({ channel }: { channel: ComedyChannel }) {
  const [publications, setPublications] = useState<ComedyPublication[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try { setPublications((await api.comedy.publications(channel.id, filter || undefined)).publications); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id, filter]);

  const sync = async () => {
    setBusy(true); setError(null); setMessage('');
    try { const result = await api.comedy.syncPublications(channel.id); setMessage(`Zsynchronizowano: ${result.sent} wysłanych, ${result.failed} błędów, ${result.cancelled} anulowanych.`); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const cancel = async (publication: ComedyPublication) => {
    if (!window.confirm('Anulować tę publikację? Jeśli slot jeszcze nie minął, nie zostanie wysłana.')) return;
    setBusy(true);
    try { await api.comedy.cancelPublication(channel.id, publication.id); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <ComedySection
      title="Publikacje"
      description="Wyniki pochodzą z istniejącego publishera Kontentowo. Brak potwierdzonego external_id oznacza brak realnej publikacji."
      actions={<div className="btn-row"><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="">wszystkie</option><option value="SCHEDULED">SCHEDULED</option><option value="SENT">SENT</option><option value="FAILED">FAILED</option><option value="CANCELLED">CANCELLED</option></select><Button size="sm" onClick={() => void load()}>Odśwież</Button><Button size="sm" variant="primary" onClick={() => void sync()} disabled={busy}>Synchronizuj wyniki</Button></div>}
    >
      {error ? <ErrorAlert error={error} /> : null}
      {message ? <div className="alert alert--success">{message}</div> : null}
      {loading ? <Spinner label="Ładowanie publikacji…" /> : publications.length === 0 ? <ComedyEmpty title="Brak publikacji" hint="Zatwierdź film i zaplanuj publikację w Harmonogramie." /> : (
        <div className="table-wrap" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead><tr><th>Platforma</th><th>Profil</th><th>Tytuł</th><th>Status</th><th>Termin</th><th>Publikacja</th><th>Błąd</th><th /></tr></thead>
            <tbody>{publications.map((publication) => (
              <tr key={publication.id}>
                <td>{COMEDY_PLATFORM_LABEL[publication.platform] ?? publication.platform}</td>
                <td>{publication.accountName ?? publication.socialAccountId}</td>
                <td style={{ maxWidth: 280 }}>{publication.title}<div className="muted" style={{ fontSize: 11 }}>{publication.scenarioTitle ?? ''}</div></td>
                <td><ComedyStatus value={publication.status} /></td>
                <td>{formatDate(publication.scheduledAt ?? publication.publishedAt)}</td>
                <td>{publication.publicUrl ? <a href={publication.publicUrl} target="_blank" rel="noreferrer">Otwórz</a> : publication.externalId ? publication.externalId : '—'}</td>
                <td className="muted" style={{ fontSize: 11, maxWidth: 220 }}>{publication.error ?? ''}</td>
                <td>{['SCHEDULED', 'APPROVED', 'DRAFT'].includes(publication.status) && <Button size="sm" variant="danger" onClick={() => void cancel(publication)}>Anuluj</Button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </ComedySection>
  );
}

export function ComedyAnalyticsPanel({ channel }: { channel: ComedyChannel }) {
  const [days, setDays] = useState(28);
  const [data, setData] = useState<ComedyAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try { setData(await api.comedy.analytics(channel.id, days)); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id, days]);

  const sync = async () => {
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.syncAnalytics(channel.id, days);
      setMessage(`Zapisano metryki dla ${result.stored} publikacji.${result.unavailable.length ? ` Niedostępne: ${result.unavailable.slice(0, 3).join('; ')}` : ''}`);
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection
        title="Analytics"
        description="Wyłącznie metryki udostępniane przez platformy (YouTube Analytics API, post_metrics z Sociamonials). Brak danych nie jest zastępowany estymacją."
        actions={<div className="btn-row"><select value={days} onChange={(event) => setDays(Number(event.target.value))}><option value={7}>7 dni</option><option value={28}>28 dni</option><option value={90}>90 dni</option></select><Button size="sm" onClick={() => void load()}>Odśwież</Button><Button size="sm" variant="primary" onClick={() => void sync()} disabled={busy}>{busy ? 'Synchronizacja…' : 'Pobierz metryki'}</Button></div>}
      >
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        {loading ? <Spinner label="Ładowanie analityki…" /> : !data ? null : (
          <>
            <div className="grid grid--stats">
              <ComedyStat label="Publikacje z metrykami" value={data.totals.publications} />
              <ComedyStat label="Wyświetlenia" value={data.totals.views} />
              <ComedyStat label="Polubienia" value={data.totals.likes} />
              <ComedyStat label="Komentarze" value={data.totals.comments} />
              <ComedyStat label="Udostępnienia" value={data.totals.shares} />
              <ComedyStat label="Nowi obserwujący" value={data.totals.followersGained} />
            </div>
            {data.totals.publications === 0 ? (
              <div className="alert alert--info" style={{ marginTop: 12 }}>Brak zmierzonych publikacji w tym okresie. {data.availability.youtubeAnalytics ? 'YouTube Analytics jest skonfigurowany — sprawdź, czy publikacje mają external_id.' : 'YouTube Analytics nie jest skonfigurowany (wymaga zakresu yt-analytics.readonly).'}</div>
            ) : null}
            <div className="grid grid--2" style={{ marginTop: 16 }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Według platformy</div>
                <table className="table"><thead><tr><th>Platforma</th><th>Publikacje</th><th>Wyświetlenia</th><th>Retencja</th></tr></thead>
                  <tbody>{data.byPlatform.map((entry) => <tr key={entry.platform}><td>{COMEDY_PLATFORM_LABEL[entry.platform] ?? entry.platform}</td><td>{entry.publications}</td><td>{entry.views}</td><td>{entry.averageRetentionPercent != null ? `${entry.averageRetentionPercent.toFixed(1)}%` : '—'}</td></tr>)}</tbody>
                </table>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Według formatu</div>
                <table className="table"><thead><tr><th>Format</th><th>Publikacje</th><th>Wyświetlenia</th><th>Engagement</th></tr></thead>
                  <tbody>{data.byFormat.map((entry) => <tr key={entry.format}><td>{entry.format}</td><td>{entry.publications}</td><td>{entry.views}</td><td>{entry.averageEngagementRate != null ? `${entry.averageEngagementRate.toFixed(2)}%` : '—'}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
            <div className="grid grid--2" style={{ marginTop: 16 }}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Długość filmu</div>
                <table className="table"><thead><tr><th>Zakres</th><th>Publikacje</th><th>Wyświetlenia</th><th>Retencja</th></tr></thead>
                  <tbody>{data.byLength.map((entry) => <tr key={entry.bucket}><td>{entry.bucket}</td><td>{entry.publications}</td><td>{entry.views}</td><td>{entry.averageRetentionPercent != null ? `${entry.averageRetentionPercent.toFixed(1)}%` : '—'}</td></tr>)}</tbody>
                </table>
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Godziny publikacji (czas kanału)</div>
                <table className="table"><thead><tr><th>Godzina</th><th>Publikacje</th><th>Wyświetlenia</th></tr></thead>
                  <tbody>{data.byHour.map((entry) => <tr key={entry.hour}><td>{String(entry.hour).padStart(2, '0')}:00</td><td>{entry.publications}</td><td>{entry.views}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
            <div className="alert alert--info" style={{ marginTop: 16 }}>
              <strong>Wskazówki (heurystyka):</strong> najlepsza godzina: {data.recommendations.bestHour != null ? `${String(data.recommendations.bestHour).padStart(2, '0')}:00` : 'brak danych'} · format: {data.recommendations.bestFormat ?? 'brak danych'} · długość: {data.recommendations.bestLength ?? 'brak danych'}. {data.recommendations.note}
            </div>
          </>
        )}
      </ComedySection>
    </div>
  );
}

export function ComedySettingsPanel({ onChanged }: { onChanged: () => void }) {
  const [stored, setStored] = useState<{ version: number; config: Record<string, unknown> } | null>(null);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try { const value = await api.comedy.settings(); setStored({ version: value.version, config: value.config }); setConfig(value.config); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const update = (key: string, value: unknown) => setConfig((current) => ({ ...current, [key]: value }));
  const save = async () => {
    if (!stored) return;
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.updateSettings({ version: stored.version, config });
      setStored({ version: result.version, config: result.config }); setConfig(result.config);
      setMessage('Ustawienia globalne zapisane.'); onChanged();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  if (loading) return <Spinner label="Ładowanie ustawień…" />;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection title="Ustawienia globalne Comedy Studio" description="Tryb globalny jest domyślnie wyłączony. Włączenie AUTOPILOT nadal wymaga zatwierdzenia per kanał i spełnienia bramek praw/QA." actions={<Button variant="primary" size="sm" onClick={() => void save()} disabled={busy}>{busy ? 'Zapisywanie…' : 'Zapisz'}</Button>}>
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        <div className="grid grid--3">
          <label className="inline"><input type="checkbox" checked={Boolean(config.enabled)} onChange={(event) => update('enabled', event.target.checked)} /> Moduł aktywny</label>
          <label className="inline"><input type="checkbox" checked={config.autopilotRequireRights !== false} onChange={(event) => update('autopilotRequireRights', event.target.checked)} /> AUTOPILOT wymaga praw</label>
          <label className="inline"><input type="checkbox" checked={config.autopilotRequireQaPass !== false} onChange={(event) => update('autopilotRequireQaPass', event.target.checked)} /> AUTOPILOT wymaga QA PASS</label>
        </div>
        <div className="form-row" style={{ marginTop: 12 }}>
          <Field label="Domyślny tryb automatyzacji"><select value={String(config.defaultAutomationMode ?? 'SEMI_AUTO')} onChange={(event) => update('defaultAutomationMode', event.target.value)}><option value="MANUAL">MANUAL</option><option value="SEMI_AUTO">SEMI_AUTO</option><option value="AUTOPILOT">AUTOPILOT</option></select></Field>
          <Field label="Domyślny tryb tworzenia"><select value={String(config.defaultCreationMode ?? 'HYBRID_AUTO')} onChange={(event) => update('defaultCreationMode', event.target.value)}><option value="HYBRID_AUTO">HYBRID_AUTO</option><option value="STORY_FIRST">STORY_FIRST</option><option value="FOOTAGE_FIRST">FOOTAGE_FIRST</option></select></Field>
        </div>
        <div className="form-row">
          <Field label="Maks. równoczesnych renderów"><input type="number" min={0} max={5} value={Number(config.maxConcurrentRenders ?? 1)} onChange={(event) => update('maxConcurrentRenders', Number(event.target.value))} /></Field>
          <Field label="Maks. researchy na tick"><input type="number" min={0} max={5} value={Number(config.maxResearchRunsPerTick ?? 1)} onChange={(event) => update('maxResearchRunsPerTick', Number(event.target.value))} /></Field>
        </div>
        <div className="form-row">
          <Field label="Maks. scenariuszy na tick"><input type="number" min={0} max={10} value={Number(config.maxScenariosPerTick ?? 2)} onChange={(event) => update('maxScenariosPerTick', Number(event.target.value))} /></Field>
          <Field label="Maks. produkcji na tick"><input type="number" min={0} max={5} value={Number(config.maxProductionsPerTick ?? 1)} onChange={(event) => update('maxProductionsPerTick', Number(event.target.value))} /></Field>
        </div>
        <Field label="Oznaczenie parodii / fikcyjnego montażu" hint="Wypalane w filmie i dodawane do opisów."><textarea rows={3} value={String(config.disclosureText ?? '')} onChange={(event) => update('disclosureText', event.target.value)} /></Field>
        <div className="muted" style={{ fontSize: 12 }}>Wersja konfiguracji: {stored?.version ?? 0}. Zapis używa kontroli wersji (409 przy równoczesnej zmianie).</div>
      </ComedySection>

      <ComedySection title="Integracje i wymagane zmienne" description="Te elementy wymagają zewnętrznych uprawnień lub konfiguracji serwera. Aplikacja pokazuje realny status, nie atrapę.">
        <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
          <li><strong>YOUTUBE_API_KEY</strong> — odkrywanie materiałów (YouTube Data API, limity quota).</li>
          <li><strong>YouTube OAuth</strong> — publikacja i Analytics; połącz konto w Social Accounts (zakres <code>yt-analytics.readonly</code> dla metryk).</li>
          <li><strong>SOCIAMONIALS_API_KEY / SOCIAMONIALS_WORKSPACE_ID</strong> — Facebook, Instagram, TikTok i metryki postów.</li>
          <li><strong>PUBLISH_LIVE</strong> — musi zawierać YOUTUBE i/lub SOCIAMONIALS dla realnej publikacji; inaczej publisher działa w trybie mock i wynik nie jest publiczny.</li>
          <li><strong>QUEUE_VIDEO_RENDER</strong> — kolejka renderera; bez niej render i transkrypcja nie zostaną uruchomione.</li>
          <li><strong>AUTH_MAGIC_LINK_SECRET</strong> — podpisywanie URL-i mediów dla renderera.</li>
          <li><strong>Lokalny whisper.cpp na VPS</strong> — <code>COMEDY_WHISPER_BIN</code> i <code>COMEDY_WHISPER_MODEL</code> (lub istniejące <code>PINS_WHISPER_*</code>).</li>
        </ul>
      </ComedySection>
    </div>
  );
}
